import { MutableRefObject, useEffect } from "react"
import { useMap } from "@vis.gl/react-google-maps"

import { DEFAULT_CAR_TRAIL_CONFIG, CarTrailState, trailSegments } from "@/services/map/car-trail"
import { colors } from "@/theme"
import { prefersReducedMotion } from "@/utils/reducedMotion"

/**
 * CarTrailOverlay — Ignition Red comet-tail trail behind the driving car
 * (PLA-273, ADR-0030 §8; ADR-0033 follow-up).
 *
 * TILT-SAFE VECTOR RENDERER. The tail is drawn with `google.maps.Polyline`s —
 * the same vector primitive AnimatedRouteLine uses — so it is rendered by the
 * map's own GL engine and stays glued to the (tilted, 3D) ground plane. The
 * previous canvas/OverlayView renderer projected geo→screen on a flat-Mercator
 * pane and drifted off the road under tilt; this renderer needs no projection
 * math and survives the full zoom-17 / 32.5° city view, so the tail is now
 * always on while live-driving.
 *
 * SMOOTH FADE WITHOUT DOTS. A `Polyline` has a single opacity + width, so the
 * head→tail taper is built from a small pool of NESTED, OVERLAPPING polylines:
 * each is the full trail truncated to a newer suffix. The longest (full) line is
 * thin + faint; each shorter line is thicker and sits on top, so the overlaps
 * composite into a continuous gradient — opaque ~6px head fading to a faint ~2px
 * tail. Because every layer is ONE continuous line (round joins, not per-segment
 * caps), there is no dotting/banding — it reads like the old canvas gradient.
 *
 * The buffer is OWNED elsewhere: SmoothCarMarker pushes every displayed pose into
 * the shared `trail` ref (the tail traces the displayed spline path, never raw
 * fixes) and its speed gate drains the tail while parked/stale. This component
 * only reads + renders. Reduced motion is re-checked per frame so a live
 * preference flip blanks the tail immediately even if buffered points haven't
 * aged out yet.
 */

interface CarTrailOverlayProps {
  /** Shared buffer the car marker pushes displayed poses into. */
  trail: MutableRefObject<CarTrailState>
  /** True only during live-driving phases — same gate as the route shimmer. */
  active: boolean
}

/** Stroke width of the newest (head) layer, in CSS px. */
const TRAIL_HEAD_WIDTH_PX = 6
/** Stroke width of the longest (tail) layer. */
const TRAIL_TAIL_WIDTH_PX = 2
/** Number of nested layers approximating the continuous fade. */
const TRAIL_LAYERS = 8
/**
 * zIndex base for the tail. Above the route arc (zIndex 1) and its shimmer dot
 * (zIndex 2), below the car marker (zIndex 100). Shorter/brighter layers sit on
 * top so the head paints over the tail.
 */
const TRAIL_Z_BASE = 3
/**
 * Per-layer opacity, derived so that all {@link TRAIL_LAYERS} stacked at the head
 * composite up to ~maxAlpha: 1 - (1 - perLayer)^N = maxAlpha.
 */
const TRAIL_LAYER_OPACITY = 1 - Math.pow(1 - DEFAULT_CAR_TRAIL_CONFIG.maxAlpha, 1 / TRAIL_LAYERS)

/** Linear lerp of the tail→head width by layer fraction (0 = full/tail). */
const layerWidth = (i: number): number =>
  TRAIL_TAIL_WIDTH_PX + (TRAIL_HEAD_WIDTH_PX - TRAIL_TAIL_WIDTH_PX) * (i / (TRAIL_LAYERS - 1))

export const CarTrailOverlay = ({ trail, active }: CarTrailOverlayProps) => {
  const map = useMap()

  useEffect(() => {
    if (!map || !active) return undefined
    // Needs the maps library; nothing to render without Polyline.
    if (!google?.maps?.Polyline) return undefined

    // Nested layers, created once. Layer 0 = full trail (thin, faint, bottom);
    // higher layers = shorter newer suffixes (thicker, on top). Opacity + width
    // are constant per layer (set here); only the path churns per frame.
    const layers: google.maps.Polyline[] = []
    const layerEmpty: boolean[] = []
    for (let i = 0; i < TRAIL_LAYERS; i++) {
      layers.push(
        new google.maps.Polyline({
          path: [],
          geodesic: false,
          clickable: false,
          strokeColor: colors.primary, // #FF382F (Ignition Red)
          strokeOpacity: TRAIL_LAYER_OPACITY,
          strokeWeight: layerWidth(i),
          zIndex: TRAIL_Z_BASE + i,
          map,
        }),
      )
      layerEmpty.push(true)
    }

    /** Applies a path to layer `i`, skipping redundant blank→blank churn. */
    const setLayerPath = (i: number, path: google.maps.LatLngLiteral[]) => {
      if (path.length === 0 && layerEmpty[i]) return
      layers[i]!.setPath(path)
      layerEmpty[i] = path.length === 0
    }

    const maxAlpha = DEFAULT_CAR_TRAIL_CONFIG.maxAlpha

    let frameId: number | null = null
    const drawFrame = () => {
      frameId = requestAnimationFrame(drawFrame)

      // Reduced motion: keep the tail blank (checked per frame so a live
      // preference flip clears buffered-but-unaged points immediately).
      if (prefersReducedMotion()) {
        for (let i = 0; i < TRAIL_LAYERS; i++) setLayerPath(i, [])
        return
      }

      // Segments are oldest -> newest with monotonically RISING alpha.
      const segments = trailSegments(trail.current, Date.now())
      if (segments.length === 0) {
        for (let i = 0; i < TRAIL_LAYERS; i++) setLayerPath(i, [])
        return
      }

      // One continuous point list (contiguous: each seg.from === prev seg.to).
      const pts: google.maps.LatLngLiteral[] = [
        { lat: segments[0]!.from.lat, lng: segments[0]!.from.lng },
      ]
      for (const seg of segments) pts.push({ lat: seg.to.lat, lng: seg.to.lng })

      // Each layer is the suffix of `pts` whose segments clear an alpha floor.
      // alpha rises toward the newest segment, so the floor selects a suffix.
      for (let i = 0; i < TRAIL_LAYERS; i++) {
        const floor = (maxAlpha * i) / TRAIL_LAYERS
        let s0 = 0
        while (s0 < segments.length && segments[s0]!.alpha < floor) s0++
        setLayerPath(i, pts.slice(s0))
      }
    }

    frameId = requestAnimationFrame(drawFrame)

    return () => {
      if (frameId !== null) cancelAnimationFrame(frameId)
      for (const line of layers) line.setMap(null)
    }
  }, [map, active, trail])

  // The polylines are managed imperatively — nothing to render in the tree.
  return null
}
